import { z } from "zod";
import {
    AccountingReportComparison,
    AccountingReportComparisonDiff,
    AccountingReportCycle,
    AccountingReportRatioType,
    MoneyDirection,
} from "@common/constants";
import { AmountRoundingLevel } from "@common/types/transactionRule";
import { zQueryBoolean } from "@common/types/filters/general";
import { ClassFilterType } from "@common/types/class";
import {
    AccountClass,
    AccountSubtype,
} from "@common/constants/domains/accounting/accounts";
import {
    AccountRolePresence,
    AccountRulePresence,
    AccountSource,
    AccountStatus,
} from "@common/types/domains/accounting/account";
import { form1099BoxFilterValueSchema } from "@common/schemas/domains/accounting/account.schema";
import { ChartOfAccountsColumn } from "@common/types/views/chartOfAccountsView";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { ScheduleJeSubtype } from "@common/types/domains/accounting/schedule";
import {
    JournalEntrySourceFilter,
    JournalEntrySourceSchema,
} from "@common/schemas/domains/accounting/journalEntry";
import {
    accountSchema,
    accountTypeSchema,
} from "@common/schemas/domains/accounting/account.schema";
import { ProfitLossTableReportSection } from "@common/dto/reports/reports-common.dto";
import { ReportWithNestedGroupsRowGroup } from "@common/types/reports/types";
import { ReportFormat } from "@common/helpers/reports";
import { CashFlowSection } from "@common/helpers/domains/accounting/cashFlowClassification";
import {
    reportPresentationSchema,
    withRequiredPresentation,
} from "@common/schemas/domains/reportSettings/report-presentation.schema";
import { ReportSettingRowVisibility } from "@common/types/reports/report-settings";

// Bound only on the six individual XLSX export contracts. Keeping it off GET
// and the tax package is deliberate: neither surface applies it.
const reportRowVisibilityQueryField = {
    rowVisibility: z.nativeEnum(ReportSettingRowVisibility).optional(),
};

export const chartOfAccountsExportQuery = z.object({
    entityIds: z.array(z.coerce.number()),
    basis: z.nativeEnum(AccountingLedgerBasis).optional(),
    ledgerId: z.string().uuid().optional(),
    availableClassifications: z.array(z.nativeEnum(AccountClass)).optional(),
    accountStatus: z.nativeEnum(AccountStatus).optional(),
    accountSource: z.nativeEnum(AccountSource).optional(),
    accountRolePresence: z.nativeEnum(AccountRolePresence).optional(),
    accountRulePresence: z.nativeEnum(AccountRulePresence).optional(),
    accountSubtypes: z.array(z.nativeEnum(AccountSubtype)).optional(),
    form1099Boxes: z.array(form1099BoxFilterValueSchema).optional(),
    search: z.string().optional(),
    coaTemplateGroupKeys: z.array(z.string()).optional(),
    columns: z.array(z.nativeEnum(ChartOfAccountsColumn)).optional(),
});

export const accountingReportQuery = z.object({
    startDate: z.string(),
    endDate: z.string(),
    entityId: z.coerce.number(),
    cycle: z.nativeEnum(AccountingReportCycle).optional(),
    comparison: z.nativeEnum(AccountingReportComparison).optional(),
    comparisonDiff: z.nativeEnum(AccountingReportComparisonDiff).optional(),
    classes: z.array(z.string()).optional(),
    classFilterType: z.nativeEnum(ClassFilterType).optional(),
    ledgerId: z.string().uuid(),
    format: z.nativeEnum(ReportFormat).optional(),
    counterpartiesIds: z.array(z.string().uuid()).optional(),
    withEmptyOrUnverifiedCounterparty: z.coerce.boolean().optional(),
    hasCounterparty: z.coerce.boolean().optional(),
});

const financialAccountFilterFields = {
    financialAccountIds: z
        .array(z.coerce.number().int().positive())
        .max(500)
        .optional(),
    subaccountIds: z.array(z.string().uuid()).max(500).optional(),
};

export const accountingReportFinancialAccountQuery =
    accountingReportQuery.extend(financialAccountFilterFields);

const multiComparisonFields = {
    comparisons: z
        .array(z.nativeEnum(AccountingReportComparison))
        .max(2)
        .optional(),
    comparisonDiffs: z
        .array(z.nativeEnum(AccountingReportComparisonDiff))
        .max(2)
        .optional(),
    ratioTypes: z
        .array(z.nativeEnum(AccountingReportRatioType))
        .max(2)
        .optional(),
};

export const accountingReportMultiComparisonQuery =
    accountingReportFinancialAccountQuery.extend({
        ...multiComparisonFields,
        // When present, the P&L / Balance Sheet report is generated for the
        // consolidation group: per-member entity columns plus an Eliminations
        // column and a post-elimination Consolidated Total column. workspaceId
        // accompanies it so the request resolves the selected workspace (these
        // routes are otherwise entity-scoped) to gate the consolidated layout.
        consolidationGroupId: z.string().uuid().optional(),
        workspaceId: z.string().uuid().optional(),
    });

export const profitLossReportQuery =
    accountingReportMultiComparisonQuery.extend({
        includeCounterpartyBreakdown: z.coerce.boolean().optional(),
    });

export const accountingReportFinancialAccountExportQuery =
    accountingReportFinancialAccountQuery.extend(reportRowVisibilityQueryField);

export const accountingReportMultiComparisonExportQuery =
    accountingReportMultiComparisonQuery.extend(reportRowVisibilityQueryField);

// Marks the role of a report column in a consolidated report. Absent on
// non-consolidated reports (the column is an ordinary entity/period/total).
export const reportColumnKindSchema = z.enum([
    "entity",
    "eliminations",
    "consolidated",
]);
export type ReportColumnKind = z.infer<typeof reportColumnKindSchema>;

export const classColumnRoleSchema = z.enum([
    "leaf",
    "parentDirect",
    "parentTotal",
    "unclassified",
]);
export type ClassColumnRole = z.infer<typeof classColumnRoleSchema>;

// Ancestor chain, root first. An ancestor not itself filtered in still appears,
// as a header-only row with no column of its own.
export const classHeaderPathEntrySchema = z.object({
    classId: z.string(),
    label: z.string(),
});
export type ClassHeaderPathEntry = z.infer<typeof classHeaderPathEntrySchema>;

export const reportPeriodWithLabelSchema = z.object({
    label: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    comparisonType: z.nativeEnum(AccountingReportComparison).optional(),
    comparisonDiff: z.nativeEnum(AccountingReportComparisonDiff).optional(),
    isTotal: z.boolean().optional(),
    entityId: z.number().optional(),
    ledgerBasis: z.nativeEnum(AccountingLedgerBasis).optional(),
    ledgerName: z.string().optional(),
    classIds: z.array(z.string()).optional(),
    classHeaderPath: z.array(classHeaderPathEntrySchema).optional(),
    classColumnRole: classColumnRoleSchema.optional(),
    ratioType: z.nativeEnum(AccountingReportRatioType).optional(),
    columnKind: reportColumnKindSchema.optional(),
});

export const generalLedgerFiltersSchema = z.object({
    classes: z.array(z.string()).optional(),
    classFilterType: z.nativeEnum(ClassFilterType).optional(),
    counterpartiesIds: z.array(z.string().uuid()).optional(),
    withEmptyOrUnverifiedCounterparty: z.coerce.boolean().optional(),
    hasCounterparty: z.coerce.boolean().optional(),
    accountCodes: z.array(z.coerce.number()).optional(),
    sourceTypes: z.array(z.nativeEnum(JournalEntrySourceFilter)).optional(),
    minAmount: z.coerce.number().optional(),
    maxAmount: z.coerce.number().optional(),
    direction: z.nativeEnum(MoneyDirection).optional(),
    amountRounded: zQueryBoolean().optional(),
    amountRoundingLevel: z.nativeEnum(AmountRoundingLevel).optional(),
    excludeFrom1099: zQueryBoolean().optional(),
    /** Filter entries to those whose transaction is on one of these financial accounts or subaccounts. */
    ...financialAccountFilterFields,
});

export const getAccountTransactionsQuery = generalLedgerFiltersSchema.extend({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    format: z.nativeEnum(ReportFormat).optional(),
    classifications: z.array(z.nativeEnum(AccountClass)).optional(),
    pnlBalanceSince: z.string().optional(),
    ledgerId: z.string().uuid(),
    entityIds: z.array(z.coerce.number().int().positive()).min(1).max(100),
    /** Filter entries to those linked to a specific set of transaction IDs. */
    transactionIds: z.array(z.coerce.number().int().positive()).optional(),
    /** Filter entries to those linked to these invoices. */
    invoiceIds: z.array(z.string().uuid()).optional(),
    /**
     * With `invoiceIds`, also include journal entries of transactions matched
     * to those invoices.
     */
    shouldIncludeMatchedTransactions: zQueryBoolean().optional(),
    /** Attribution filter: schedule-tagged lines plus the re-routed source lines of these applications. */
    scheduleApplicationIds: z.array(z.string().uuid()).optional(),
    /** Narrows schedule-tagged lines to these subtypes. */
    scheduleJeSubtypes: z.array(z.nativeEnum(ScheduleJeSubtype)).optional(),
    /** Filter entries whose description (or memo) contains this text. */
    search: z
        .string()
        .trim()
        .max(256)
        .refine((value) => value.length === 0 || value.length >= 2, {
            message: "Search must be at least 2 characters",
        })
        .optional(),
});

export const getAccountTransactionsExportQuery =
    getAccountTransactionsQuery.extend(reportRowVisibilityQueryField);

export const accountTransactionsLineSchema = z.object({
    id: z.string(),
    /** @deprecated Use `dateOnly` for the accounting calendar day. */
    date: z.coerce.date(),
    dateOnly: z.string().nullable().optional(),
    entityId: z.number(),
    accountCode: z.number(),
    description: z.string(),
    memo: z.string().nullable(),
    referenceNumber: z.string(),
    debitAmount: z.number(),
    creditAmount: z.number(),
    balance: z.number(),
    split: z.array(z.number()),
    source: JournalEntrySourceSchema,
    splitLabel: z.string(),
    bankDescription: z.string().nullable(),
    counterpartyName: z.string().nullable(),
    excludeFrom1099: z.boolean(),
    classIds: z.array(z.string()),
    classes: z.array(
        z.object({
            id: z.string(),
            label: z.string(),
        }),
    ),
    recurringRuleId: z.string().nullable().optional(),
});

export const accountTransactionsSchema = z.object({
    openingBalance: z.number(),
    closingBalance: z.number(),
    transactions: z.array(accountTransactionsLineSchema),
});

export const accountWithTransactionsSchema = z.object({
    account: accountSchema,
    entityIds: z.array(z.number()),
    isAccountIncreasingByDebit: z.boolean(),
    transactions: accountTransactionsSchema,
});

export const getAccountTransactionsResponseSchema = z.object({
    accountsWithTransactions: z.array(accountWithTransactionsSchema),
    presentation: reportPresentationSchema.optional(),
});

export const getAccountTransactionsResponseWithPresentationSchema =
    withRequiredPresentation(getAccountTransactionsResponseSchema);

export const extendedAccountSchema = accountSchema.extend({
    debit: z.number(),
    credit: z.number(),
    balance: z.number(),
    entityId: z.number(),
    hasOpeningBalance: z.boolean(),
    hasScheduleReferences: z.boolean().optional(),
});

export const chartOfAccountsEntityGroupSchema = z.object({
    id: z.string().uuid(),
    entityId: z.number().int(),
    name: z.string(),
    type: accountTypeSchema,
    parentGroupId: z.string().uuid().nullable(),
    sortOrder: z.number(),
});
export type ChartOfAccountsEntityGroup = z.infer<
    typeof chartOfAccountsEntityGroupSchema
>;

export const reportWithNestedGroupsRowLineSchema = z.object({
    label: z.string(),
    key: z.string(),
    values: z.array(z.number()),
    accountId: z.string().nullable().optional(),
    groupId: z.string().nullable().optional(),
});

export const chartOfAccountsResponseSchema = z.object({
    accounts: z.array(extendedAccountSchema),
    groups: z.array(chartOfAccountsEntityGroupSchema),
});

export const trialBalanceResponseSchema = z.object({
    periods: z.array(reportPeriodWithLabelSchema),
    rows: z.array(reportWithNestedGroupsRowLineSchema),
    presentation: reportPresentationSchema.optional(),
});

export const trialBalanceResponseWithPresentationSchema =
    withRequiredPresentation(trialBalanceResponseSchema);

export const expenseByVendorExportQuery =
    accountingReportMultiComparisonQuery.extend({
        sort: z.string().optional(),
    });

export const expenseByVendorRowVisibilityExportQuery =
    expenseByVendorExportQuery.extend(reportRowVisibilityQueryField);

export const expenseByVendorRowSchema = z.object({
    vendorId: z.string().nullable(),
    vendorName: z.string(),
    values: z.array(z.number()),
});

export const expenseByVendorResponseSchema = z.object({
    periods: z.array(reportPeriodWithLabelSchema),
    rows: z.array(expenseByVendorRowSchema),
    totals: z.array(z.number()),
    presentation: reportPresentationSchema.optional(),
});

export const expenseByVendorResponseWithPresentationSchema =
    withRequiredPresentation(expenseByVendorResponseSchema);

export const reportWithNestedGroupsRowGroupSchema: z.ZodType<ReportWithNestedGroupsRowGroup> =
    z.object({
        label: z.string(),
        rows: z.array(
            z.union([
                z.lazy(() => reportWithNestedGroupsRowGroupSchema),
                reportWithNestedGroupsRowLineSchema,
            ]),
        ),
        totals: z.array(z.number()).optional(),
    });

export const balanceSheetResponseSchema = z.object({
    periods: z.array(reportPeriodWithLabelSchema),
    rows: z.array(reportWithNestedGroupsRowGroupSchema),
    presentation: reportPresentationSchema.optional(),
});

export const balanceSheetResponseWithPresentationSchema =
    withRequiredPresentation(balanceSheetResponseSchema);

// Profit and Loss schemas
export const plCellSchema = z.object({
    value: z.number(),
    label: z.string(),
});

export type PlRowKind = "group" | "account" | "counterparty";

export interface PlRow {
    data: Array<z.infer<typeof plCellSchema>>;
    category: string;
    counterpartyId?: string | null;
    counterpartyName?: string | null;
    meta?: { code: number };
    children?: PlRow[];
    // Optional on the wire so older payloads remain valid; absent ⇒ "account".
    kind?: PlRowKind;
}

export const plRowSchema: z.ZodType<PlRow> = z.lazy(() =>
    z.object({
        data: z.array(plCellSchema),
        category: z.string(),
        counterpartyId: z.string().nullable().optional(),
        counterpartyName: z.string().nullable().optional(),
        meta: z
            .object({
                code: z.number(),
            })
            .optional(),
        children: z.array(plRowSchema).optional(),
        kind: z.enum(["group", "account", "counterparty"]).optional(),
    }),
);

export const plSectionSchema = z.object({
    rows: z.array(plRowSchema).optional(),
    data: z.array(plCellSchema),
    meta: z
        .object({
            summaryOfSections: z
                .array(z.nativeEnum(ProfitLossTableReportSection))
                .optional(),
        })
        .optional(),
});

export const profitLossReportMetaSchema = z.object({
    dataStart: z.string().nullable(),
    dataEnd: z.string().nullable().optional(),
});

export const profitLossTableReportSectionsSchema = z.record(
    z.nativeEnum(ProfitLossTableReportSection),
    plSectionSchema,
);

export const profitLossResponseSchema = z.object({
    periods: z.array(reportPeriodWithLabelSchema),
    sections: profitLossTableReportSectionsSchema,
    meta: profitLossReportMetaSchema,
    presentation: reportPresentationSchema.optional(),
});

export const profitLossResponseWithPresentationSchema =
    withRequiredPresentation(profitLossResponseSchema);

// Cash Flow Statement schemas (indirect method)
export const cashFlowStatementSummarySchema = z.object({
    operatingActivities: z.array(z.number()),
    investingActivities: z.array(z.number()),
    financingActivities: z.array(z.number()),
    netCashChange: z.array(z.number()),
    beginningCashBalance: z.array(z.number()),
    endingCashBalance: z.array(z.number()),
});

export const cashFlowStatementSectionSchema = z.object({
    section: z.nativeEnum(CashFlowSection),
    rows: z.array(
        z.union([
            reportWithNestedGroupsRowGroupSchema,
            reportWithNestedGroupsRowLineSchema,
        ]),
    ),
    totals: z.array(z.number()),
});

export const cashFlowStatementResponseSchema = z.object({
    periods: z.array(reportPeriodWithLabelSchema),
    sections: z.array(cashFlowStatementSectionSchema),
    summary: cashFlowStatementSummarySchema,
    presentation: reportPresentationSchema.optional(),
});

export const cashFlowStatementResponseWithPresentationSchema =
    withRequiredPresentation(cashFlowStatementResponseSchema);

export const bankReconciliationReportTransactionSchema = z.object({
    id: z.number(),
    date: z.string(),
    description: z.string(),
    counterparty: z.string().nullable(),
    amount: z.number(),
});

export const bankReconciliationReportTransactionGroupSchema = z.object({
    deposits: z.array(bankReconciliationReportTransactionSchema),
    payments: z.array(bankReconciliationReportTransactionSchema),
    depositsTotal: z.number(),
    paymentsTotal: z.number(),
});

export const bankReconciliationReportResponseSchema = z.object({
    entityId: z.number(),
    ledgerId: z.string().nullable(),
    entityName: z.string(),
    accountName: z.string(),
    accountNumberMask: z.string().nullable(),
    periodOpeningDate: z.string(),
    periodClosingDate: z.string(),
    openingBalance: z.number(),
    closingBalance: z.number(),
    statementEndingBalance: z.number(),
    hasStatementBalance: z.boolean(),
    bookBalance: z.number(),
    isLiability: z.boolean(),
    totalUnreconciled: z.number(),
    differenceToReconcile: z.number().nullable(),
    preparedByName: z.string().nullable(),
    preparedDate: z.string(),
    cleared: bankReconciliationReportTransactionGroupSchema,
    uncleared: bankReconciliationReportTransactionGroupSchema,
});
